/* @flow */
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import idx from 'idx';

import ScrollLoadZone from 'components/ScrollLoadZone.jsx';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';
import CenteredContainer from 'components/CenteredContainer.jsx';
import ErrorReporter from 'app/components/ErrorReporter.jsx';
import Icon from 'components/Icon.jsx';
import Text from 'components/Text.jsx';
import GET_ORG_MEMBERS_QUERY from './queries/OrganizationMembersQuery.gql';

import type {
  GetOrganizationMembers,
  GetOrganizationMembers_organizations_members_members,
} from './queries/__generated__/GetOrganizationMembers.js';

import {
  ComposableTable,
  getFamilyCellGenerator,
  getIconCellGenerator,
  getTextCellGenerator,
  getTimestampCellGenerator,
} from 'components/ComposableTable';

import type { ApolloQueryResult } from 'apollo-client';

type EntityType = 'organization' | 'group';
type UserRole = 'teacher' | 'student' | 'parent' | 'admin' | 'unknown';

type Props = {
  entityType: EntityType,
  entityUuid: string,
  grade: ?string,
  query: ?string,
  userRole: UserRole,
  rostered: ?string,
  hasNextPage: boolean,
  fetchMore: () => Promise<any>,
  members: GetOrganizationMembers_organizations_members_members[],
  loading: boolean,
  error: errorFragmentFragment,
};

class PeopleListTableDataContainer extends React.Component<Props> {
  errorMessage(error: errorFragmentFragment) {
    return (
      <CenteredContainer className="PeopleList-body">
        {error && (
          <ErrorReporter error="PeopleListTableDataContainer - couldn't load data">
            <Text>Error loading data</Text>
          </ErrorReporter>
        )}
      </CenteredContainer>
    );
  }

  flattenMembersStructure(
    members: GetOrganizationMembers_organizations_members_members[]
  ): Object[] {
    return members.map(
      ({ grade, orgUserStats, roles, rosteredStatus, user, userInteractionPermissions }) => {
        return {
          id: user.id,
          uuid: user.uuid,
          color: user.color,
          name: user.name,
          initials: user.initials,
          rosteredStatus,
          roles,
          grade,
          profilePictureUrl: user.profilePictureUrl,
          groupsOwnedCount: orgUserStats.groupsOwnedCount,
          groupsJoinedCount: orgUserStats.groupsJoinedCount,
          reachability: user.reachabilityInfo,
          familySummary: user.familySummary,
          lastBroadcastSentOn: orgUserStats.lastBroadcastSentOn
            ? orgUserStats.lastBroadcastSentOn * 1000
            : null,
          userInteractionPermissions,
        };
      }
    );
  }

  columnHeadersForRole(role: UserRole): Array<string> {
    if (role === 'teacher') {
      return ['Name', 'Source', 'Last announcement sent', 'Classes'];
    }
    if (role === 'student') {
      return ['Name', 'Source', 'Grade', 'Classes', 'Family members'];
    }
    if (role === 'parent') {
      return ['Name', 'Source', 'Classes', 'Family members'];
    }
    if (role === 'admin') {
      return ['Name', 'Title', 'Last announcement sent'];
    }

    // unknown
    return ['Name', 'Source', 'Role', 'Classes'];
  }

  columnGeneratorsForColumns(columnHeaders: Array<string>): Array<Function> {
    const iconProps = {
      size: 'large',
    };

    const components = {
      Name: getTextCellGenerator('name'),
      Classes: getTextCellGenerator('groupsJoinedCount'),
      'Family members': getFamilyCellGenerator('familySummary', row => ({
        userId: row.id,
        userUuid: row.uuid,
        canAddChild: row.userInteractionPermissions.canAddChild,
        canAddParent: row.userInteractionPermissions.canAddParent,
      })),
      Grade: getTextCellGenerator('grade'),
      'Last announcement sent': getTimestampCellGenerator('lastBroadcastSentOn'),
      Source: getIconCellGenerator('rosteredStatus', {
        true: <Icon {...iconProps} name="link-linked" color="shamrock" />,
        false: <Icon {...iconProps} name="link-unlinked" color="koala" />,
      }),
      Role: getTextCellGenerator('roles'),
      Title: getTextCellGenerator('title'),
    };

    return columnHeaders.map(header => components[header]);
  }

  render() {
    const { loading, error, members, userRole, hasNextPage, fetchMore } = this.props;

    const columnHeaders = this.columnHeadersForRole(userRole);
    const columnGenerators = this.columnGeneratorsForColumns(columnHeaders);

    if (error) {
      return this.errorMessage(error);
    }

    if (loading) {
      return <CenteredLoadingIndicator className="PeopleList-body" />;
    }

    if (members) {
      return (
        <div>
          <ComposableTable
            className="PeopleList-body"
            columnHeaders={columnHeaders}
            columnGenerators={columnGenerators}
            rows={this.flattenMembersStructure(members)}
            keyField="uuid"
          />
          {hasNextPage && (
            <ScrollLoadZone threshold={500} onZoneActivated={fetchMore}>
              <CenteredLoadingIndicator height={50} size="small" />
            </ScrollLoadZone>
          )}
        </div>
      );
    } else {
      return this.errorMessage(error);
    }
  }
}

const generateProps = (
  data: ApolloQueryResult<GetOrganizationMembers>,
  contentsKey: 'organizations' | 'groups'
) => {
  return {
    fetchMore: () =>
      data.fetchMore({
        variables: {
          after: idx(data[contentsKey], _ => _[0].members.pageInfo.endCursor),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const fetchMoreContents = idx(fetchMoreResult[contentsKey], _ => _[0]);
          const prevContents = idx(prev[contentsKey], _ => _[0]);

          if (!fetchMoreContents || !prevContents) {
            return prev;
          }

          const newResult = Object.assign({}, prev);

          // Concat member arrays and assign to new object members key
          const newMembersContents = Object.assign({}, fetchMoreContents.members, {
            members: [...prevContents.members.members, ...fetchMoreContents.members.members],
          });

          // Assign { members: [] } to outer members key in structure.
          // Structure is
          // members: {
          //   members: [],
          //   pageInfo: {},
          // }
          const newResultContents = Object.assign({}, fetchMoreContents, {
            members: newMembersContents,
          });

          // Assign new result to the first element in the organizations array since
          // most outer structure is
          // organizations: [
          //  {
          //    uuid
          //    members: { ... as above }
          //  }
          // ]
          newResult[contentsKey] = [newResultContents];

          return newResult;
        },
      }),
    hasNextPage: idx(data[contentsKey], _ => _[0].members.pageInfo.hasNextPage),
    members: idx(data[contentsKey], _ => _[0].members.members),
    loading: data.loading,
    error: data.error,
  };
};

const withOrganizationMembers = graphql(GET_ORG_MEMBERS_QUERY, {
  options: ({ entityUuid, grade, query, userRole, rostered }) => {
    return {
      variables: {
        orgUuid: entityUuid,
        first: 20,
        after: null,
        query,
        roles: [userRole],
        grade,
        rostered,
        attributeName: 'id',
        direction: 'asc',
      },
    };
  },
  skip: ({ entityType }) => entityType !== 'organization',
  props: ({ data }) => generateProps(data, 'organizations'),
});

export { PeopleListTableDataContainer };

export default compose(withOrganizationMembers)(PeopleListTableDataContainer);
